import useHeader from '@hooks/useHeader';
import { useTranslation } from 'react-i18next';
import { useSuspenseQuery } from '@tanstack/react-query';
import { Box, Card, Divider, Stack } from '@mui/material';
import ViewListItem from '@Components/General/ViewListItem';
import { TaskAltRounded } from '@mui/icons-material';
import Tag from '@Components/Common/Tag';
import { useMemo, useState } from 'react';
import EditGroup from './EditGroup';
import GroupAvatar from './Components/GroupAvatar';
import { useLoaderDeps } from '@tanstack/react-router';
import { contactsGroupsQueryOptions } from '@utils/queryOptions';
import Toolbar from '@Components/General/Toolbar';
import SimpleSort from '@Components/Common/SimpleSort';
import SimplePagination from '@Components/Common/SimplePagination';
import useSimplePagination from '@hooks/useSimplePagination';
import useSimpleSort from '@hooks/useSimpleSort';
import useEmptySearchParams from '@hooks/useEmptySearchParams';
import NoGroups from './Components/no-groups';
import NoGroupsFound from './Components/no-groups-found';

const Groups = () => {
    const { t } = useTranslation();
    const [newGroup, setNewGroup] = useState(false);

    const simplePageHook = useSimplePagination();

    const searchParamsEmpty = useEmptySearchParams();

    const simpleSortHook = useSimpleSort({
        inverseFields: ['created', 'updated'],
        defaultSortField: 'name',
    });

    const {
        data: { results: groups, count },
    } = useSuspenseQuery(contactsGroupsQueryOptions(useLoaderDeps({})));

    const headerProps = useMemo(() => {
        return {
            title: t('Groups'),
            searchProps: {
                searchPlacholder: t('search:contacts-groups'),
            },
            fabAction: () => () => setNewGroup(true),
        };
    }, []);

    useHeader(headerProps);

    if (groups.length === 0 && searchParamsEmpty) {
        return (
            <>
                <NoGroups action={() => setNewGroup(true)} />
                <EditGroup open={newGroup} onClose={() => setNewGroup(false)} />
            </>
        );
    }

    return (
        <Box flexGrow={1}>
            <Box display="flex" flexDirection="column" height="100%">
                <Toolbar>
                    <SimpleSort
                        sortOptions={[
                            { value: 'name', label: t('Name') },
                            { value: 'created', label: t('Last Created') },
                            { value: 'updated', label: t('Last Updated') },
                        ]}
                        {...simpleSortHook}
                    />
                    <SimplePagination count={count} {...simplePageHook} />
                </Toolbar>
                {groups.length === 0 ? (
                    <NoGroupsFound />
                ) : (
                    <Card>
                        <Stack divider={<Divider />}>
                            {groups.map(group => (
                                <ViewListItem
                                    key={group.id}
                                    title={group.name}
                                    description={`${group.count} ${t('Contacts')}${
                                        group.description
                                            ? ' • ' + group.description
                                            : ''
                                    }`}
                                    height={64}
                                    avatar={<GroupAvatar group={group} />}
                                    to={group.id}
                                    tags={
                                        group.todo ? (
                                            <Tag
                                                label={group.todo_count?.toLocaleString()}
                                                color="secondary"
                                                variant="tonal"
                                                icon={<TaskAltRounded />}
                                            />
                                        ) : null
                                    }
                                />
                            ))}
                        </Stack>
                    </Card>
                )}
                <EditGroup open={newGroup} onClose={() => setNewGroup(false)} />
            </Box>
        </Box>
    );
};

export default Groups;
